import { i18n } from "@tellescope/i18n";
import type { IntegrationName, OrgPlanName } from "@tellescope/tanstack-query";
import { Integration, IntegrationBase, IntegrationConfig, IntegrationTag } from "./types";
import healthieLogoUrl from "./assets/healthie-logo.png?w=72&format=webp&imagetools";
import { createKeyMap } from "@tellescope/utils";
import { TextAndCallIcon } from "./components/TextAndCallIcon";

export const getAllIntegrations = () =>
  [
    {
      name: "healthie",
      color: "#8b5cf6",
      logoSrc: healthieLogoUrl,
      title: i18n.t("Healthie", { ns: "onboarding" }),
      description: i18n.t("Practice management platform", { ns: "onboarding" }),
      tags: [{ value: "ehr", label: i18n.t("EHR", { ns: "onboarding" }) }],
    },
    {
      name: "twilio",
      color: "#3b82f6",
      Icon: TextAndCallIcon,
      title: i18n.t("Text & Call", { ns: "onboarding" }),
      description: i18n.t("Text and call your patients", { ns: "onboarding" }),
      tags: [{ value: "communication", label: i18n.t("Communication", { ns: "onboarding" }) }],
    },
  ] as const satisfies readonly (IntegrationBase & { name: IntegrationName })[];
export const getAllIntegrationsMap = () => createKeyMap(getAllIntegrations(), "name");
export const getAllIntegrationsTags = (integrations?: Integration[]) =>
  (integrations ?? getAllIntegrations()).reduce<Record<IntegrationTag, string>>(
    (acc, { tags }) => {
      tags.forEach((tag) => {
        if (!acc[tag.value]) {
          acc[tag.value] = tag.label;
        }
      });
      return acc;
    },
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {} as Record<IntegrationTag, string>
  );

/**
 * The integrations a plan offers, merged with their presentation data, in the
 * order they appear during setup. The plan→integration mapping (with
 * `required`/`default` flags) lives here.
 *
 * @param plan The org plan.
 * @returns Ordered {@link IntegrationConfig}s for the plan.
 */
export const getPlanIntegrationConfigs = (plan: OrgPlanName): IntegrationConfig[] => {
  const allIntegrations = getAllIntegrationsMap();

  return (
    {
      "Healthie-Text": [
        { ...allIntegrations["healthie"], required: true, default: true },
        { ...allIntegrations["twilio"], required: true, default: true },
      ],
    } as const satisfies Record<OrgPlanName, IntegrationConfig[]>
  )[plan];
};
